import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import FilterIcon from "../../../assets/Icons/Orange Filter.png";
import CrossIcon from "../../../assets/Icons/cross_svg.svg";
import LeftCircle from "../../../assets/Icons/Left circle B.png";
import RightCircle from "../../../assets/Icons/Right circle A.png";
import ButtonTriangle from "../../../assets/Icons/Button Triangle.png";
import CustomAlert from "../../../helpers/CustomAlert";
import { axiosPostApi } from "../../../helpers/interceptorServices";
import { apiList } from "../../../helpers/apiList";
import { formatDate } from "../../../helpers/helper";
import Dropdown from "../../../components/Dropdown";
import { format, parse, isValid } from "date-fns";

// Helper function to get date 7 days ago in YYYY-MM-DD format
const getLast7DaysDate = () => {
  const today = new Date();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const year = sevenDaysAgo.getFullYear();
  const month = String(sevenDaysAgo.getMonth() + 1).padStart(2, "0");
  const day = String(sevenDaysAgo.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to get today's date in YYYY-MM-DD format
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to format date from YYYY-MM-DD to DD-MM-YYYY for API
const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

const NotificationMasterFilterModal = ({
  show,
  handleClose,
  setData,
  setPageDetails,
  setNoData,
  pageSize,
  currentPage,
  filterData,
  setFilterData,
  handleReset,
  setCurrentPage,
  fetchNotificationData,
}) => {
  const loggedInUserName = sessionStorage.getItem("username");

  // Status options for dropdown
  const statusOptions = [
    { label: "Arrived at Hub", value: "ARRIVED_AT_HUB" },
    {
      label: "Handed Over to Delivery Boy",
      value: "HANDED_OVER_TO_DELIVERY_BOY",
    },
    { label: "Allocated to Delivery Boy", value: "ALLOCATED_TO_DELIVERY_BOY" },
    { label: "Notified Hub", value: "NOTIFIED_HUB" },
    { label: "Opened at Hub", value: "OPENED_AT_HUB" },
  ];

  const getDefaultFromDate = () => {
    if (filterData?.fromDate) {
      return filterData.fromDate;
    }
    return getLast7DaysDate();
  };

  const getDefaultToDate = () => {
    if (filterData?.toDate) {
      return filterData.toDate;
    }
    return getTodayDate();
  };

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      bagNo: filterData.bagNo || "",
      fromDate: getDefaultFromDate(),
      toDate: getDefaultToDate(),
      status: filterData.status || "",
    },
    mode: "onChange",
  });

  const [selectedTab, setSelectedTab] = useState("Bag No");
  const [loading, setLoading] = useState(false);

  const watchedValues = watch();

  const hasActiveFilter = (tabName) => {
    switch (tabName) {
      case "Bag No":
        return watchedValues.bagNo && watchedValues.bagNo.trim() !== "";
      case "Transaction Date":
        return (
          (watchedValues.fromDate &&
            watchedValues.fromDate !== getDefaultFromDate()) ||
          (watchedValues.toDate && watchedValues.toDate !== getDefaultToDate())
        );
      case "Status":
        return watchedValues.status && watchedValues.status !== "";
      default:
        return false;
    }
  };

  const handleTabChange = (e, tabName) => {
    e.preventDefault();
    setSelectedTab(tabName);
  };

  const getAvailableTabs = () => {
    return ["Bag No", "Transaction Date", "Status"];
  };

  const fetchFilteredData = async (page, size, filters) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
      };

      if (filters?.bagNo?.trim()) {
        requestBody.bagNo = filters.bagNo.trim();
      }

      // Always send fromDate and toDate
      const fromDateToUse = filters?.fromDate || getLast7DaysDate();
      const toDateToUse = filters?.toDate || getTodayDate();

      requestBody.fromDate = formatDateForAPI(fromDateToUse);
      requestBody.toDate = formatDateForAPI(toDateToUse);

      if (filters?.status) {
        requestBody.statusList = [filters.status];
      }

      const response = await axiosPostApi(
        `${apiList.FETCH_NOTIFICATION_MASTER}?&pageSize=${size}&pageNo=${page}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setData(modifiedData);
        setNoData(modifiedData?.length === 0);
      } else {
        setData([]);
        setNoData(true);
        if (response?.data?.respCode !== "01") {
          CustomAlert({
            title: "Failed",
            text: response?.data?.message || "Failed to fetch data",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      }
    } catch (error) {
      setData([]);
      setNoData(true);
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
    } finally {
      setLoading(false);
    }
  };

  const modifyStructure = (response) => {
    return response?.map((user, index) => {
      let formattedCreDate = null;
      let formattedStrDate = null;

      if (user?.createdDate) {
        const parsed = parse(
          user.createdDate,
          "dd-MM-yyyy HH:mm:ss",
          new Date(),
        );
        if (isValid(parsed)) {
          formattedCreDate = parsed;
        }
      }

      if (user?.startDateTime) {
        const parsed = parse(
          user.startDateTime,
          "dd-MM-yyyy HH:mm:ss",
          new Date(),
        );
        if (isValid(parsed)) {
          formattedStrDate = parsed;
        }
      }

      const itemCount = user.itemsIncluded?.length || 0;

      const itemsIncludedIds = Array.isArray(user.itemsIncluded)
        ? user.itemsIncluded
        : [];

      return {
        ...user,
        itemsIncludedIds,
        createdDate: formattedCreDate
          ? [
              format(formattedCreDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedCreDate, "HH:mm:ss"),
            ]
          : "",
        startDateTime: formattedStrDate
          ? [
              format(formattedStrDate, "dd-MM-yyyy"),
              <br key={index} />,
              format(formattedStrDate, "HH:mm:ss"),
            ]
          : "",
        itemsIncluded: (
          <span
            className="text-blue-600 underline cursor-pointer"
            onClick={() => handleItemsIncludedClick(user)}
          >
            {itemCount > 0
              ? `${itemCount} Item${itemCount > 1 ? "s" : ""}`
              : "No Items"}
          </span>
        ),
        creatorDetailsMobile: user?.creatorDetails?.mobileNo || "",
      };
    });
  };

  const handleItemsIncludedClick = (row) => {
    // This will be passed from parent if needed
  };

  const onSubmit = (data) => {
    const formattedData = {
      bagNo: data.bagNo || "",
      fromDate: data.fromDate || "",
      toDate: data.toDate || "",
      status: data.status || "",
    };

    setFilterData(formattedData);
    setCurrentPage(1);
    fetchFilteredData(1, pageSize, formattedData);
    handleClose();
  };

  const onReset = () => {
    const defaultFromDate = getLast7DaysDate();
    const defaultToDate = getTodayDate();

    reset({
      bagNo: "",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      status: "",
    });

    const defaultFilterData = {
      bagNo: "",
      fromDate: defaultFromDate,
      toDate: defaultToDate,
      status: "",
    };

    setFilterData(defaultFilterData);
    setCurrentPage(1);
    fetchFilteredData(1, pageSize, defaultFilterData);
  };

  useEffect(() => {
    if (show) {
      setValue("fromDate", getDefaultFromDate());
      setValue("toDate", getDefaultToDate());
      setValue("bagNo", filterData?.bagNo || "");
      setValue("status", filterData?.status || "");
    }
  }, [
    show,
    filterData?.fromDate,
    filterData?.toDate,
    filterData?.bagNo,
    filterData?.status,
    setValue,
  ]);

  useEffect(() => {
    if (show) {
      setSelectedTab("Bag No");
    }
  }, [show]);

  return (
    <>
      {show && (
        <div
          style={{
            position: "fixed",
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1060,
          }}
        >
          <div className="modal-dialog" role="document">
            <div
              className="modal-content"
              style={{
                width: "40vw",
                borderRadius: "24px",
                padding: "24px",
                backgroundColor: "#FFF",
                border: "1px solid rgba(0, 0, 0, 0.175)",
              }}
            >
              <img
                src={LeftCircle}
                alt="leftImg"
                className="absolute left-0 bottom-0 w-[110px] h-[105px]"
              />
              <img
                src={RightCircle}
                alt="rightImg"
                className="absolute right-0 top-0 w-[110px] h-[100px]"
              />
              <div
                className="modal-header"
                style={{
                  padding: "1rem 1rem",
                  borderBottom: "1px solid #dee2e6",
                  justifyContent: "space-between",
                  display: "flex",
                }}
              >
                <div className="flex items-center gap-2">
                  <img src={FilterIcon} alt="filter icon" className="w-6 h-6" />
                  <h5 className="modal-title main-title font-medium">
                    Filters
                  </h5>
                </div>
                <img
                  src={CrossIcon}
                  alt="close icon"
                  className="w-[30px] h-[30px] cursor-pointer z-20"
                  onClick={handleClose}
                />
              </div>
              <Box component="form" onSubmit={handleSubmit(onSubmit)}>
                <div
                  className="scoped-modal modal-body"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <div className="tab-container">
                    <ul className="tab-list">
                      {getAvailableTabs().map((tab) => (
                        <div className="tab-wrapper" key={tab}>
                          <li
                            className={
                              selectedTab === tab
                                ? "tab-item selected"
                                : "tab-item"
                            }
                            style={
                              hasActiveFilter(tab) && selectedTab !== tab
                                ? {
                                    outline: "2px solid #FF6B35",
                                    outlineOffset: "2px",
                                  }
                                : {}
                            }
                          >
                            <button
                              className={`tab-button ${
                                selectedTab === tab ? "active" : ""
                              }`}
                              onClick={(e) => handleTabChange(e, tab)}
                            >
                              {tab}
                            </button>
                          </li>
                          {selectedTab === tab && (
                            <img
                              src={ButtonTriangle}
                              alt="tri"
                              className="tab-triangle"
                            />
                          )}
                        </div>
                      ))}
                    </ul>

                    <div className="tab-content !bg-[linear-gradient(to_bottom,#FFE8D4_0%_100%,transparent_25%_100%)]">
                      {selectedTab === "Bag No" && (
                        <Controller
                          name="bagNo"
                          control={control}
                          rules={{
                            pattern: {
                              value: /^[A-Za-z0-9_]+$/,
                              message:
                                "Bag number must contain only letters, numbers and underscore",
                            },
                            maxLength: {
                              value: 50,
                              message: "Bag number cannot exceed 50 characters",
                            },
                          }}
                          render={({ field }) => (
                            <div className="dateField">
                              <label className="dateLabel">Bag Number</label>
                              <div className="dateInputWrapper">
                                <input
                                  {...field}
                                  type="text"
                                  placeholder="Enter Bag Number"
                                  className="dateInput"
                                  maxLength={50}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /[^A-Za-z0-9_]/g,
                                      "",
                                    );
                                    field.onChange(val);
                                  }}
                                />
                                {errors.bagNo && (
                                  <div className="error-text">
                                    {errors.bagNo.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        />
                      )}

                      {selectedTab === "Transaction Date" && (
                        <div
                          className="dateGrid"
                          style={{
                            display: "flex",
                            flexDirection: "row",
                            gap: "1rem",
                            alignItems: "flex-start",
                          }}
                        >
                          <Controller
                            name="fromDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid From Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "From Date cannot be in the future",
                                beforeToDate: (value, formValues) =>
                                  !value ||
                                  !formValues.toDate ||
                                  new Date(value) <=
                                    new Date(formValues.toDate) ||
                                  "From Date must be before To Date",
                                requiredIfToDate: (value, formValues) =>
                                  !formValues.toDate ||
                                  !!value ||
                                  "From Date is required when To Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">From Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.fromDate && (
                                    <div className="error-text">
                                      {errors.fromDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                          <Controller
                            name="toDate"
                            control={control}
                            rules={{
                              validate: {
                                validDate: (value) =>
                                  !value ||
                                  !isNaN(Date.parse(value)) ||
                                  "Invalid To Date",
                                notFuture: (value) =>
                                  !value ||
                                  new Date(value) <= new Date() ||
                                  "To Date cannot be in the future",
                                afterFromDate: (value, formValues) =>
                                  !value ||
                                  !formValues.fromDate ||
                                  new Date(value) >=
                                    new Date(formValues.fromDate) ||
                                  "To Date must be after From Date",
                                requiredIfFromDate: (value, formValues) =>
                                  !formValues.fromDate ||
                                  !!value ||
                                  "To Date is required when From Date is selected",
                              },
                            }}
                            render={({ field }) => (
                              <div className="dateField" style={{ flex: 1 }}>
                                <label className="dateLabel">To Date</label>
                                <div className="dateInputWrapper">
                                  <input
                                    {...field}
                                    type="date"
                                    className="dateInput"
                                    max={getTodayDate()}
                                    onChange={(e) => {
                                      field.onChange(e.target.value);
                                    }}
                                  />
                                  {errors.toDate && (
                                    <div className="error-text">
                                      {errors.toDate.message}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          />
                        </div>
                      )}

                      {selectedTab === "Status" && (
                        <Controller
                          name="status"
                          control={control}
                          render={({ field }) => (
                            <div className="dateField">
                              <Dropdown
                                label="Status"
                                options={statusOptions}
                                value={field.value}
                                labelKey="label"
                                valueKey="value"
                                placeholder="Select Status"
                                onChange={(item) => {
                                  field.onChange(item.value);
                                }}
                              />
                            </div>
                          )}
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="scoped-modal"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    padding: "12px",
                    paddingBottom: "0px",
                    gap: "16px",
                    borderTop: "1px solid #dee2e6",
                  }}
                >
                  <div className="buttonContainer">
                    <button
                      type="button"
                      className="btn btn-back !rounded-lg w-[133px]"
                      onClick={onReset}
                      disabled={loading}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="buttonContainer">
                    <button
                      type="submit"
                      className="btn btn-submit !rounded-lg w-[133px]"
                      disabled={loading}
                    >
                      {loading ? (
                        <CircularProgress
                          style={{ width: "20px", height: "20px" }}
                        />
                      ) : (
                        "Apply"
                      )}
                    </button>
                  </div>
                </div>
              </Box>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NotificationMasterFilterModal;
